/**
 * Format an interval given in seconds into a compact human-readable string.
 *
 * Examples:
 *   30        → "30s"
 *   60        → "1 min"
 *   300       → "5 min"
 *   3600      → "1 hr"
 *   7200      → "2 hr"
 *   86400     → "1 day"
 *   604800    → "1 week"
 *   90        → "1 min 30s"
 *   3661      → "1 hr 1 min"
 */
export function fmtInterval(seconds) {
  if (!seconds || seconds <= 0) return "—";

  const WEEK = 604800;
  const DAY  = 86400;
  const HOUR = 3600;
  const MIN  = 60;

  const parts = [];

  if (seconds >= WEEK && seconds % WEEK === 0) {
    const n = seconds / WEEK;
    return `${n} ${n === 1 ? "week" : "weeks"}`;
  }
  if (seconds >= DAY && seconds % DAY === 0) {
    const n = seconds / DAY;
    return `${n} ${n === 1 ? "day" : "days"}`;
  }
  if (seconds >= HOUR) {
    const hrs = Math.floor(seconds / HOUR);
    const rem = seconds % HOUR;
    parts.push(`${hrs} ${hrs === 1 ? "hr" : "hr"}`);
    if (rem >= MIN) parts.push(`${Math.floor(rem / MIN)} min`);
    else if (rem > 0) parts.push(`${rem}s`);
    return parts.join(" ");
  }
  if (seconds >= MIN) {
    const mins = Math.floor(seconds / MIN);
    const rem  = seconds % MIN;
    parts.push(`${mins} min`);
    if (rem > 0) parts.push(`${rem}s`);
    return parts.join(" ");
  }
  return `${seconds}s`;
}

/**
 * Format a duration in milliseconds.
 *   < 1 s    → "123 ms"
 *   < 60 s   → "1.2 s"
 *   ≥ 60 s   → "1m 8s"
 */
export function fmtMs(ms) {
  if (ms == null) return "—";
  if (ms < 1000) return `${Math.round(ms)} ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)} s`;
  const m = Math.floor(ms / 60000);
  const s = Math.round((ms % 60000) / 1000);
  return s === 0 ? `${m}m` : `${m}m ${s}s`;
}

const DEFAULT_TIME_ZONE = "Asia/Jerusalem";
const DEFAULT_DATE_LOCALE = "en-GB";

function normalizeDate(value) {
  if (value == null || value === "") return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDate(value, options, locale = DEFAULT_DATE_LOCALE) {
  const date = normalizeDate(value);
  if (!date) return "—";

  return new Intl.DateTimeFormat(locale, {
    timeZone: DEFAULT_TIME_ZONE,
    ...options,
  }).format(date);
}

export function fmtDateTime(value, locale = DEFAULT_DATE_LOCALE) {
  return formatDate(value, { dateStyle: "short", timeStyle: "short" }, locale);
}

export function fmtDate(value, locale = DEFAULT_DATE_LOCALE) {
  return formatDate(value, { dateStyle: "short" }, locale);
}

export function fmtTime(value, locale = DEFAULT_DATE_LOCALE) {
  return formatDate(value, { timeStyle: "short" }, locale);
}
